import { inject, provide, reactive, readonly, ref, type InjectionKey } from 'vue'

import type { DashboardGridAncestor } from '../models/builder.types'
import type { DashboardRow } from '../models/layout.types'
import {
  canMoveRow,
  canMoveTab,
  moveRow,
  moveTab,
  samePath,
  type RowMoveSource,
  type RowMoveTarget,
  type TabMoveSource,
  type TabMoveTarget,
} from '../service/layout/moveLayout'

export type LayoutDragItem = ({ kind: 'row' } & RowMoveSource) | ({ kind: 'tab' } & TabMoveSource)
export type LayoutDropTarget = ({ kind: 'row' } & RowMoveTarget) | ({ kind: 'tab' } & TabMoveTarget)

/**
 * One drag session shared by every grid level, the tab strips and the outline panel.
 *
 * The old grid nested one `Sortable` per level, each owning its own source/target refs. A
 * `dragstart` on a nested section bubbled into every ancestor `Sortable`, so a drop reordered the
 * outer list as well, and since each list only knew its own items nothing could cross a level.
 * A single session over the ROOT rows removes both problems: whoever is under the pointer names a
 * target by path, and the drop is one `moveRow`/`moveTab` over the whole tree.
 */
export interface LayoutDrag {
  readonly item: Readonly<{ value: LayoutDragItem | null }>
  readonly target: Readonly<{ value: LayoutDropTarget | null }>
  /** The selected tab per cell, keyed by {@link cellKey}; shared so a drop can reveal its result. */
  readonly activeTabs: Record<string, string>
  start: (item: LayoutDragItem, event: DragEvent) => void
  /** Claim the pointer for `target`. Returns false — and leaves the event alone — when refused. */
  over: (target: LayoutDropTarget, event: DragEvent) => boolean
  /** The pointer is over chrome that is no drop target: hide any stale indicator. */
  clear: () => void
  drop: (event: DragEvent) => void
  end: () => void
}

const LAYOUT_DRAG: InjectionKey<LayoutDrag> = Symbol('dashboards.layoutDrag')

/** Dragging needs SOME payload for Firefox to start; the real one lives in the session. */
const DRAG_MIME = 'application/x-orphex-dashboard-layout'

export function cellKey(
  ancestors: readonly DashboardGridAncestor[],
  rowId: string,
  cellId: string,
): string {
  return [
    ...ancestors.map((step) => `${step.rowId}/${step.cellId}/${step.tabId}`),
    rowId,
    cellId,
  ].join('>')
}

/**
 * A remembered id is only usable while its tab still exists. Deleting the active tab used to
 * leave the map pointing at a dead id, and since the panel is chosen by `id === active`, the
 * cell rendered no panel at all — a blank block with a live tab strip above it.
 */
export function resolveActiveTab(
  activeTabs: Readonly<Record<string, string>>,
  key: string,
  tabs: readonly { id: string }[],
): string {
  const remembered = activeTabs[key]
  return tabs.some((tab) => tab.id === remembered) && remembered !== undefined
    ? remembered
    : (tabs[0]?.id ?? '')
}

/** True when dropping here would leave the tree exactly as it is. */
function isNoop(
  item: LayoutDragItem,
  target: LayoutDropTarget,
  rows: readonly DashboardRow[],
): boolean {
  if (!samePath(item.ancestors, target.ancestors)) return false
  if (item.kind === 'row' && target.kind === 'row') {
    const from = gridRows(rows, item.ancestors)?.findIndex((row) => row.id === item.rowId) ?? -1
    return target.index === from || target.index === from + 1
  }
  if (item.kind === 'tab' && target.kind === 'tab') {
    if (item.rowId !== target.rowId || item.cellId !== target.cellId) return false
    const tabs = gridRows(rows, item.ancestors)
      ?.find((row) => row.id === item.rowId)
      ?.cells.find((cell) => cell.id === item.cellId)?.tabs
    const from = tabs?.findIndex((tab) => tab.id === item.tabId) ?? -1
    return target.index === from || target.index === from + 1
  }
  return false
}

function gridRows(
  rows: readonly DashboardRow[],
  ancestors: readonly DashboardGridAncestor[],
): readonly DashboardRow[] | null {
  let current = rows
  for (const step of ancestors) {
    const tab = current
      .find((row) => row.id === step.rowId)
      ?.cells.find((cell) => cell.id === step.cellId)
      ?.tabs.find((entry) => entry.id === step.tabId)
    if (!tab) return null
    current = tab.rows
  }
  return current
}

function accepts(item: LayoutDragItem, target: LayoutDropTarget): boolean {
  if (item.kind === 'row' && target.kind === 'row') return canMoveRow(item, target)
  if (item.kind === 'tab' && target.kind === 'tab') return canMoveTab(item, target)
  return false
}

export function createLayoutDrag(options: {
  rows: () => readonly DashboardRow[]
  commit: (rows: DashboardRow[]) => void
}): LayoutDrag {
  const item = ref<LayoutDragItem | null>(null)
  const target = ref<LayoutDropTarget | null>(null)
  const activeTabs = reactive<Record<string, string>>({})

  function start(next: LayoutDragItem, event: DragEvent): void {
    event.stopPropagation()
    item.value = next
    target.value = null
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData(DRAG_MIME, next.kind)
    }
  }

  function over(next: LayoutDropTarget, event: DragEvent): boolean {
    const current = item.value
    if (!current || !accepts(current, next)) return false
    event.preventDefault()
    event.stopPropagation()
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
    target.value = isNoop(current, next, options.rows()) ? null : next
    return true
  }

  function end(): void {
    item.value = null
    target.value = null
  }

  /** Reveal what was dropped: the moved tab, or the tab that now holds the moved section. */
  function reveal(dropped: LayoutDragItem, at: LayoutDropTarget): void {
    if (dropped.kind === 'tab' && at.kind === 'tab') {
      activeTabs[cellKey(at.ancestors, at.rowId, at.cellId)] = dropped.tabId
      return
    }
    const owner = at.ancestors.at(-1)
    if (owner) {
      activeTabs[cellKey(at.ancestors.slice(0, -1), owner.rowId, owner.cellId)] = owner.tabId
    }
  }

  function commit(dropped: LayoutDragItem, at: LayoutDropTarget): void {
    const rows = options.rows()
    if (dropped.kind === 'row' && at.kind === 'row') options.commit(moveRow(rows, dropped, at))
    else if (dropped.kind === 'tab' && at.kind === 'tab') options.commit(moveTab(rows, dropped, at))
    else return
    reveal(dropped, at)
  }

  function drop(event: DragEvent): void {
    const dropped = item.value
    const at = target.value
    end()
    if (!dropped || !at) return
    event.preventDefault()
    event.stopPropagation()
    commit(dropped, at)
  }

  return {
    item: readonly(item),
    target: readonly(target),
    activeTabs,
    start,
    over,
    clear: () => (target.value = null),
    drop,
    end,
  }
}

export function provideLayoutDrag(drag: LayoutDrag): LayoutDrag {
  provide(LAYOUT_DRAG, drag)
  return drag
}

export function injectLayoutDrag(): LayoutDrag | null {
  return inject(LAYOUT_DRAG, null)
}

/** Position helpers: which half of `element` the pointer is in, along one axis. */
export function isAfterMidpoint(event: DragEvent, element: Element, axis: 'x' | 'y'): boolean {
  const rect = element.getBoundingClientRect()
  return axis === 'y'
    ? event.clientY > rect.top + rect.height / 2
    : event.clientX > rect.left + rect.width / 2
}

/**
 * The drop side of one grid level. Every pointer position inside the grid that a nested grid or a
 * tab strip did not claim lands on its rows container — a row's header, its padding, the gap
 * between rows — and resolves to the slot whose midpoint the pointer has passed. Hit-testing the
 * container instead of each row is what makes the gaps droppable and keeps a nested grid's claim
 * from leaking into its parent.
 */
export function useGridRowDrop(
  drag: LayoutDrag,
  grid: { readonly path: readonly DashboardGridAncestor[]; readonly rows: readonly unknown[] },
) {
  const path = () => grid.path
  const rowCount = () => grid.rows.length

  function startRow(rowId: string, event: DragEvent): void {
    const section = (event.currentTarget as HTMLElement).closest('.dashboard-grid__row')
    if (section && event.dataTransfer) event.dataTransfer.setDragImage(section, 24, 20)
    drag.start({ kind: 'row', ancestors: [...path()], rowId }, event)
  }

  function isDraggedRow(row: DashboardRow): boolean {
    const item = drag.item.value
    return item?.kind === 'row' && item.rowId === row.id && samePath(item.ancestors, path())
  }

  /** The insertion line sits above row `index`; `rows.length` puts it below the last row. */
  function showsLineAt(index: number): boolean {
    const target = drag.target.value
    return target?.kind === 'row' && target.index === index && samePath(target.ancestors, path())
  }

  /** A line above row `index`, and below it when it is the last row and the end slot is targeted. */
  function lineEdges(index: number): ('top' | 'bottom')[] {
    const edges: ('top' | 'bottom')[] = showsLineAt(index) ? ['top'] : []
    if (index === rowCount() - 1 && showsLineAt(rowCount())) edges.push('bottom')
    return edges
  }

  /** Centred in the `gap-lg` between rows. */
  function lineClass(edge: 'top' | 'bottom'): string {
    return edge === 'top'
      ? '-top-[calc(var(--ui-space-lg)/2+1px)]'
      : '-bottom-[calc(var(--ui-space-lg)/2+1px)]'
  }

  function onRowsOver(event: DragEvent): void {
    if (drag.item.value?.kind !== 'row') return
    const container = event.currentTarget as HTMLElement
    const slots = [...container.children].filter((child) => child.matches('[data-row-slot]'))
    const index = slots.filter((slot) => isAfterMidpoint(event, slot, 'y')).length
    drag.over({ kind: 'row', ancestors: [...path()], index }, event)
  }

  /**
   * The grid's "New Section" card doubles as its end slot. It is always there in edit mode, so an
   * empty tab is droppable without a zone appearing on dragstart — a zone that shifted the layout
   * under the pointer the moment a drag began, which also made Chromium abandon the drag.
   */
  function onEndOver(event: DragEvent): void {
    if (drag.item.value?.kind !== 'row') return
    drag.over({ kind: 'row', ancestors: [...path()], index: rowCount() }, event)
  }

  return { startRow, isDraggedRow, showsLineAt, lineEdges, lineClass, onRowsOver, onEndOver }
}
